import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/hooks/useAuth";
import { useUnifiedStats } from "@/hooks/useUnifiedStats";
import { useAchievements } from "@/hooks/useAchievements";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { 
  User, BarChart3, HelpCircle, LogOut, 
  Flame, BookOpen, Info, FileText, ExternalLink
} from "lucide-react";

interface ProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentDate: string;
  puzzleNumber: number;
}

export function ProfileModal({ 
  open, 
  onOpenChange,
  currentDate,
  puzzleNumber
}: ProfileModalProps) {
  const { user, profile, isAuthenticated, signOut } = useAuth();
  const stats = useUnifiedStats();
  const { achievements, earnedAchievements } = useAchievements();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const latestAchievement = earnedAchievements.length > 0
    ? achievements.find(a => a.id === earnedAchievements[earnedAchievements.length - 1].achievement_id)
    : null;

  const handleSignOut = async () => {
    await signOut();
    onOpenChange(false);
    navigate("/play");
  };

  const handleNavigate = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const renderContent = () => (
    <div className="space-y-4">
      {/* Date & Puzzle Info - moved from header */}
      <div className="text-center pb-3 border-b">
        <p className="text-lg font-semibold">Puzzle #{puzzleNumber}</p>
        <p className="text-sm text-muted-foreground">{currentDate}</p>
      </div>

      {/* Quick Stats - Always visible */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold">{stats.totalPlayed}</div>
            <div className="text-xs text-muted-foreground">Played</div>
          </div>
          <div>
            <div className="text-2xl font-bold">{stats.winPercentage}%</div>
            <div className="text-xs text-muted-foreground">Win Rate</div>
          </div>
          <div>
            <div className="flex items-center justify-center gap-1">
              <Flame className="h-5 w-5 text-orange-500" />
              <span className="text-2xl font-bold">{stats.currentStreak}</span>
            </div>
            <div className="text-xs text-muted-foreground">Streak</div>
          </div>
        </div>
      </div>

      {/* Latest Achievement (if any) */}
      {latestAchievement && (
        <button
          onClick={() => handleNavigate('/profile#achievements')}
          className="w-full flex items-center gap-3 rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/50"
        >
          <div className="text-2xl">{latestAchievement.icon}</div>
          <div className="flex-1 text-left">
            <div className="text-sm font-medium">{latestAchievement.name}</div>
            <div className="text-xs text-muted-foreground">Latest Achievement</div>
          </div>
          <Badge variant="secondary" className="text-xs">
            {latestAchievement.tier}
          </Badge>
        </button>
      )}

      {/* Accordion Sections */}
      <Accordion type="single" collapsible className="w-full">
        {/* Full Statistics Section */}
        <AccordionItem value="statistics">
          <AccordionTrigger className="hover:no-underline">
            <span className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Full Statistics
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-muted/30 rounded p-2">
                  <div className="text-lg font-bold">{stats.totalWon}</div>
                  <div className="text-xs text-muted-foreground">Total Wins</div>
                </div>
                <div className="bg-muted/30 rounded p-2">
                  <div className="text-lg font-bold">{stats.maxStreak}</div>
                  <div className="text-xs text-muted-foreground">Best Streak</div>
                </div>
              </div>
              
              {/* Guess distribution */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Guess Distribution</p>
                {[1, 2, 3, 4, 5, 6].map((guess) => {
                  const count = stats.guessDistribution?.[guess] || 0;
                  const maxCount = Math.max(...Object.values(stats.guessDistribution || { 1: 0 }));
                  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  return (
                    <div key={guess} className="flex items-center gap-2 text-xs">
                      <span className="w-3">{guess}</span>
                      <div className="flex-1 bg-muted/30 rounded h-4 overflow-hidden">
                        <div 
                          className="h-full bg-primary/60 rounded flex items-center justify-end pr-1"
                          style={{ width: `${Math.max(percentage, count > 0 ? 10 : 0)}%` }}
                        >
                          {count > 0 && <span className="text-[10px] font-medium">{count}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {isAuthenticated && (
                <Button 
                  variant="outline" 
                  className="w-full mt-2" 
                  size="sm"
                  onClick={() => handleNavigate('/profile')}
                >
                  <ExternalLink className="h-3 w-3 mr-2" />
                  View Complete Profile
                </Button>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* How to Play */}
        <AccordionItem value="tutorial">
          <AccordionTrigger className="hover:no-underline">
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              How to Play
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2 text-sm">
              <p className="text-muted-foreground">Guess the word in 6 tries. Each guess must be a valid 5-letter word.</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-tile-correct rounded flex items-center justify-center text-tile-correct-foreground font-bold text-sm">W</div>
                  <span className="text-xs">Pink: Correct letter, correct spot</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-tile-present rounded flex items-center justify-center text-tile-present-foreground font-bold text-sm">O</div>
                  <span className="text-xs">Purple: Correct letter, wrong spot</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-tile-absent rounded flex items-center justify-center text-tile-absent-foreground font-bold text-sm">R</div>
                  <span className="text-xs">Gray: Letter not in word</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground pt-1">A new puzzle is available each day!</p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Help & Links */}
        <AccordionItem value="help">
          <AccordionTrigger className="hover:no-underline">
            <span className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              Help & More
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-1 pt-2">
              <Button 
                variant="ghost" 
                className="w-full justify-start h-9 px-2" 
                size="sm"
                onClick={() => handleNavigate('/help')}
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                Help & FAQ
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start h-9 px-2" 
                size="sm"
                onClick={() => handleNavigate('/about')}
              >
                <Info className="h-4 w-4 mr-2" />
                About WordPop
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start h-9 px-2" 
                size="sm"
                onClick={() => handleNavigate('/changelog')}
              >
                <FileText className="h-4 w-4 mr-2" />
                Changelog
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Account Section */}
        <AccordionItem value="account" className="border-b-0">
          <AccordionTrigger className="hover:no-underline">
            <span className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Account
            </span>
          </AccordionTrigger>
          <AccordionContent>
            {isAuthenticated ? (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {profile?.display_name?.charAt(0) || user?.email?.charAt(0) || "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {profile?.display_name || user?.email?.split('@')[0] || "User"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full" 
                  size="sm"
                  onClick={() => handleNavigate('/profile')}
                >
                  View Profile
                </Button>
                <Button 
                  variant="ghost" 
                  className="w-full text-destructive hover:text-destructive hover:bg-destructive/10" 
                  size="sm"
                  onClick={handleSignOut}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="space-y-3 pt-2">
                <p className="text-sm text-muted-foreground">Sign in to sync progress across devices and unlock achievements</p>
                <Button 
                  className="w-full" 
                  size="sm"
                  onClick={() => handleNavigate('/auth')}
                >
                  Sign In
                </Button>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );

  // Responsive: Drawer on mobile, Dialog on desktop
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="sr-only">Profile & Stats</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 overflow-y-auto">
            {renderContent()}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader className="pb-0">
          <DialogTitle className="sr-only">Profile & Stats</DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
